import React, { useState } from "react";
import { useObserver } from "mobx-react";
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography
} from "@material-ui/core";
import locale from "@bonliva-crm/locale";
import { RegularButton } from "@components";
import { BookingEditStore } from "./BookingEditStore";

type Props = {
  store: BookingEditStore;
};

export function DirectRequestContractModal({ store }: Props) {
  const [basicPrice, setBasicPrice] = useState("");
  const [extraInvoiceLine, setExtraInvoiceLine] = useState("");
  const [avropsnummer, setAvropsnummer] = useState("");

  const reset = () => {
    setBasicPrice("");
    setExtraInvoiceLine("");
    setAvropsnummer("");
  };

  const handleCancel = () => {
    reset();
    store.closeDirectRequestModal();
  };

  const handleCreate = async () => {
    const created = await store.createDirectRequestContract({
      basicPrice: Number(basicPrice),
      extraInvoiceLine,
      avropsnummer
    });
    // Keep the entered values on failure so the user can retry without re-typing.
    if (created) {
      reset();
    }
  };

  return useObserver(() => {
    const priceValid = basicPrice !== "" && Number(basicPrice) > 0;
    // All fields are mandatory before a direct-request contract can be saved.
    const formValid =
      priceValid && extraInvoiceLine.trim() !== "" && avropsnummer.trim() !== "";
    const saving = store.isCreatingDirectRequestContract;

    return (
      <Dialog open={store.showDirectRequestModal} onClose={handleCancel} fullWidth maxWidth="sm">
        <DialogTitle>{locale.directRequestContract.title}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary">
                {locale.directRequestContract.dateRange}: {store.directRequestWeekRange}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                required
                disabled={saving}
                label={locale.directRequestContract.basicPrice}
                value={basicPrice}
                onChange={(e) => setBasicPrice(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                disabled={saving}
                label={locale.directRequestContract.extraInvoiceLine}
                helperText={locale.directRequestContract.extraInvoiceLineHelp}
                value={extraInvoiceLine}
                onChange={(e) => setExtraInvoiceLine(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                disabled={saving}
                label={locale.directRequestContract.avropsnummer}
                value={avropsnummer}
                onChange={(e) => setAvropsnummer(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              {saving ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2">
                    {locale.directRequestContract.saving}{" "}
                    {locale.directRequestContract.durationNote}
                  </Typography>
                </div>
              ) : (
                <Typography variant="caption" color="textSecondary">
                  {locale.directRequestContract.durationNote}
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <RegularButton variant="outlined" onClick={handleCancel} disabled={saving}>
            {locale.directRequestContract.cancel}
          </RegularButton>
          <RegularButton onClick={handleCreate} disabled={!formValid || saving}>
            {locale.directRequestContract.create}
          </RegularButton>
        </DialogActions>
      </Dialog>
    );
  });
}

export default DirectRequestContractModal;
